import { Box, FieldErrorText, Spacer } from '@repo/ui/atoms';
import { Button, RadioButtons } from '@repo/ui/molecules';
import React, { useContext, useEffect, useRef, useState } from 'react';
import QuestionTitle from '../../common/QuestionTitle';

import { AnalyticsContext, MetadataContext } from '../../utils/ContextWrapper';
import { useStudyContext } from '../../utils/StudyContext';
import { formatDate, formatDateWithNewline } from '../../utils/date';
import { SupportedLanguages } from '../../utils/fetchData';
import { sendEvent } from '../../utils/sendEvent';
import { randomString } from '../../utils/utils';

import { useTranslationsContext } from '../../utils/TranslationsContext';
import DisqualifyMessage from '../DisqualifyMessage';
import Description from '../common/Description';
import {
    FieldsetStyle,
    LegendStyle,
    QuestionWrapperStyle,
} from '../stepsStyle';
import type { CalendarStepT, Slot } from '../types';

type Props = {
    isLoading?: boolean;
    step: CalendarStepT;
    selectedDate: null | string;
    setSelectedDate: (date: string) => void;
    selectedSlot: undefined | string;
    setSelectedSlot: (slot: string | undefined) => void;
    goNext: (slot: string) => void;
    apiError: null | string;
    label?: string | null;
    description?: string | null;
    event: 'calendarStep' | 'reschedule';
};

const formatDateToUserTimezone = (date: string) => {
    const formatter = new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        day: '2-digit',
        month: '2-digit',
        timeZone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    // Format the date into 'DD/MM/YYYY' format
    const formattedDate = formatter.format(new Date(date));

    // Custom formatting: rearrange into 'YYYY-day-month'
    const [day, month, year] = formattedDate.split('/');
    return `${year}-${month}-${day}`;
};

const getUniqueDates = (
    dates: Slot[],
    supportedLanguages: SupportedLanguages,
) => {
    return dates.reduce<{
        [key: string]: {
            originalDates: Slot[];
            dateName: ReturnType<typeof formatDateWithNewline>;
            formattedDate: string;
        };
    }>((acc, date) => {
        const formattedDate = formatDateToUserTimezone(date.startDate);
        if (!acc[formattedDate]) {
            acc[formattedDate] = {
                originalDates: [date],
                dateName: formatDateWithNewline(
                    date.startDate,
                    supportedLanguages,
                ),
                formattedDate,
            };
        } else {
            acc[formattedDate].originalDates.push(date);
        }
        return acc;
    }, {});
};

const SelectSlot = ({
    step,
    goNext,
    selectedDate,
    setSelectedDate,
    selectedSlot,
    setSelectedSlot,
    apiError,
    label,
    description,
    isLoading,
    event = 'calendarStep',
}: Props) => {
    const { study } = useStudyContext();
    const { t } = useTranslationsContext();
    const errorRef = useRef(randomString(3));
    const analyticsId = useContext(AnalyticsContext).id;
    const demoMode = useContext(MetadataContext).demoMode;
    const [isDisqualified, setIsDisqualified] = useState<boolean>(false);

    const [errorMessage, setErrorMessage] = useState<string>();

    const uniqueDates = getUniqueDates(step.slots, study.language);

    useEffect(() => {
        if (Object.keys(uniqueDates).length === 1) {
            const onlyDateToSelect = Object.keys(uniqueDates)[0];
            if (typeof onlyDateToSelect === 'string') {
                setSelectedDate(onlyDateToSelect);
            }
        }
    }, [uniqueDates]);

    const isAtAddress = step.locationType === 'address';

    React.useEffect(() => {
        sendEvent(
            {
                analyticsId,
                event: `embed.${event}`,
                studyId: study.id,
            },
            demoMode,
        );
    }, []);

    if (isDisqualified) {
        return <DisqualifyMessage />;
    }

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
            }}
        >
            <div className={QuestionWrapperStyle}>
                <fieldset
                    className={FieldsetStyle}
                    aria-describedby={
                        errorMessage ? errorRef.current : undefined
                    }
                >
                    <legend className={LegendStyle}>
                        <QuestionTitle
                            label={label || t('calendarStep.label')}
                        />
                    </legend>
                    <Description
                        text={
                            description ||
                            t(
                                isAtAddress
                                    ? 'calendarStep.addressDescription'
                                    : 'calendarStep.videoDescription',
                                {
                                    length: study.slotLength,
                                    address: step.location,
                                },
                            )
                        }
                    />
                    <Box gap="24" direction="column">
                        <RadioButtons
                            isRequired
                            errorMessage={errorMessage}
                            selected={selectedDate}
                            radios={Object.values(uniqueDates).map(
                                ({ dateName, formattedDate }) => {
                                    const { day, month, weekday } = dateName;
                                    const date = formattedDate;
                                    return {
                                        value: date,
                                        ariaLabel: formatDate({
                                            date,
                                            formatType: 'numericDate',
                                            lang: study.language,
                                        }),
                                        label: (
                                            <Box
                                                direction="column"
                                                alignItems="center"
                                            >
                                                <b aria-hidden>
                                                    {capitalizeFirstChar(
                                                        weekday,
                                                    )}
                                                </b>
                                                <span aria-hidden>
                                                    {day} {month}
                                                </span>
                                            </Box>
                                        ),
                                    };
                                },
                            )}
                            label={t('calendarStep.selectDate')}
                            onChange={(key) => {
                                setSelectedDate(key);
                            }}
                        />

                        {selectedDate && (
                            <RadioButtons
                                isRequired
                                errorMessage={errorMessage}
                                selected={selectedSlot}
                                radios={
                                    uniqueDates[selectedDate]
                                        ? uniqueDates[
                                              selectedDate
                                          ].originalDates.map((slot) => {
                                              return {
                                                  ariaLabel: formatDate({
                                                      date: slot.startDate,
                                                      formatType: 'timeOnly',
                                                  }),
                                                  value: slot.reference,
                                                  label: (
                                                      <span aria-hidden>
                                                          {formatDate({
                                                              date: slot.startDate,
                                                              formatType:
                                                                  'timeOnly',
                                                          })}
                                                      </span>
                                                  ),
                                              };
                                          })
                                        : []
                                }
                                label={t('calendarStep.selectTime')}
                                onChange={(key) => {
                                    setSelectedSlot(key);
                                }}
                            />
                        )}
                    </Box>

                    {apiError && (
                        <Box y="12">
                            <FieldErrorText>{apiError}</FieldErrorText>
                        </Box>
                    )}
                    <Spacer t="32" />
                    <Box gap="12" direction="column">
                        <Button
                            isLoading={isLoading}
                            type="submit"
                            onClick={() => {
                                if (selectedSlot) {
                                    goNext(selectedSlot);
                                } else {
                                    setErrorMessage(
                                        t('calendarStep.pleaseSelectTimeslot'),
                                    );
                                }
                            }}
                        >
                            {t('common.continue')}
                        </Button>
                        <Button
                            variant="tertiary"
                            onClick={() => setIsDisqualified(true)}
                        >
                            {t('common.cancel')}
                        </Button>
                    </Box>
                </fieldset>
            </div>
        </form>
    );
};

export default SelectSlot;

function capitalizeFirstChar(str: string): string {
    if (str.length === 0) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
}
